/**
 * The React binding for checkoutReducer: wires the reducer up, derives everything that is not
 * stored, and exposes one flat object of handlers for the JSX.
 *
 * The split is deliberate. checkoutReducer.js holds the RULES and imports no React, so every
 * transition is testable as data-in/data-out. This file holds the WIRING -- dispatch, memos,
 * and the two mount-time effects -- which is the part that genuinely needs a renderer.
 *
 * WHAT IS DERIVED, NEVER STORED:
 *   cart              resolveCart(selection) -- the single source of truth for money
 *   visible*          the three catalog lists filtered by the current plan/term
 *   blockers          which required fields are stopping submission
 * Storing any of these would create a second source of truth that could fall out of sync,
 * which is exactly the class of bug Phase 2 removed from pricing.
 */

import { useReducer, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  primaryProducts,
  addonSubscriptions,
  oneTimeProducts,
  questions,
  resolveCart,
  filterVisible,
} from '../../lib/pricing/index.js';
import { validateField, validateCustomer } from '../../lib/validation/customer.mjs';
import { checkoutReducer, makeInitialState, ACTIONS } from './checkoutReducer';
import { CUSTOMER_FIELD_NAMES, REQUIRED_CUSTOMER_FIELDS, CUSTOMER_FIELDS } from './checkoutSchema';
import {
  readHydration,
  writeSavedState,
  persistForCheckout,
  buildShareParams,
} from './urlPrefill';

/** The real catalog, injected into the reducer so tests can substitute a fixture. */
const CATALOG = { primaryProducts, addonSubscriptions, oneTimeProducts };

export default function useCheckoutForm({ formRef } = {}) {
  const [state, dispatch] = useReducer(checkoutReducer, CATALOG, makeInitialState);

  const { customer, selection, coupon, consent, ui } = state;

  /**
   * Hydrate from the URL or localStorage, once, in ONE dispatch.
   *
   * This effect is all that remains of two effects, a ref, and two 500ms timers. The old code
   * called ~13 individual setState functions in a loop, then set isLoadingFromUrlRef = true to
   * stop the visibility effect from stripping what it had just restored, then cleared the ref
   * on a timer and hoped every setState had flushed. Reconciliation now happens inside
   * HYDRATED, so there is no second pass to suppress and nothing to time.
   */
  useEffect(() => {
    const hydration = readHydration(window.location.search);
    if (!hydration) return;

    dispatch({ type: ACTIONS.HYDRATED, ...hydration });

    // Strip the params so a refresh does not re-apply them, and so PII does not sit in the
    // address bar (or in whatever the browser syncs from it).
    if (hydration.shouldCleanUrl) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  /**
   * Adopt browser-autofilled values once at mount.
   *
   * Chrome can paint autofilled values into inputs before React hydrates, so React state and
   * the DOM disagree and the user's details are silently dropped on submit. FormData reads
   * every named input in one call, replacing six getElementById lookups.
   *
   * Runs after the hydration effect above (effect order follows declaration order), and
   * AUTOFILL_DETECTED refuses to overwrite a non-empty field -- so a share URL's values
   * always win over whatever the browser painted.
   */
  useEffect(() => {
    const form = formRef?.current;
    if (!form) return;

    const data = new FormData(form);
    const values = {};
    CUSTOMER_FIELD_NAMES.forEach((name) => {
      const value = data.get(name);
      if (typeof value === 'string' && value) values[name] = value;
    });

    if (Object.keys(values).length > 0) {
      dispatch({ type: ACTIONS.AUTOFILL_DETECTED, values });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Persist the cart so a cancelled Stripe checkout can be resumed.
   *
   * Skipped on the very first render. Without that guard the initial default state would be
   * written over the saved blob before the hydration effect above had a chance to read it --
   * the write would win the race and the resume flow would restore an empty cart.
   *
   * Writes the CART ONLY. Customer PII is written once, at submit, by persistForCheckout --
   * see the PII note in urlPrefill.js. `customer` is deliberately absent from both the call
   * and the dependency array, so typing a name no longer triggers a localStorage write at all.
   */
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    writeSavedState({ selection, coupon, consent });
  }, [selection, coupon, consent]);

  // ---------------------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------------------

  /**
   * The authoritative cart. ONE traversal produces the displayed line items, the lookup keys,
   * and the totals, so display and payload cannot diverge. The server runs this same function
   * on the same selection, so it also cannot disagree with what the customer saw.
   */
  const cart = useMemo(() => resolveCart({ ...selection, coupon: coupon.applied }), [
    selection,
    coupon.applied,
  ]);

  const visibilityCtx = useMemo(
    () => ({ term: selection.term, primaryPlanId: selection.primaryPlanId }),
    [selection.term, selection.primaryPlanId]
  );

  const visiblePrimary = useMemo(() => filterVisible(primaryProducts, visibilityCtx), [visibilityCtx]);
  const visibleAddons = useMemo(() => filterVisible(addonSubscriptions, visibilityCtx), [visibilityCtx]);
  const visibleOnetime = useMemo(() => filterVisible(oneTimeProducts, visibilityCtx), [visibilityCtx]);

  /**
   * What is stopping submission, in the order the fields appear on the page.
   *
   * Returned as data rather than a boolean so the submit blocker can list each problem as a
   * button that focuses its own field. The old code computed this three times inline (twice
   * for `disabled`, once for the message) with a hand-written field list that could drift from
   * the schema.
   *
   * LABELS ARE NOUN PHRASES, not imperative sentences. They used to read "Enter your first
   * name" and "Agree to the Terms of Use and Privacy Policy", which works when each one owns a
   * bulleted line but not in the redesign's panel, where they run together after a "Still
   * needed:" prefix. "Still needed: first name, terms agreement" reads; "Still needed: Enter
   * your first name, Agree to the Terms of Use and Privacy Policy" does not.
   *
   * Each label is also the accessible name of a button that focuses the field, so they have to
   * stand alone as well as compose -- which is why an invalid field says "a valid email address"
   * rather than reusing the field's own validation message. The specific message stays on the
   * field itself, where the customer is sent to fix it.
   */
  const blockers = useMemo(() => {
    const list = [];

    if (!selection.primaryPlanId) {
      list.push({ field: null, label: 'a plan' });
    }

    REQUIRED_CUSTOMER_FIELDS.forEach((name) => {
      const fieldName = CUSTOMER_FIELDS[name].label.toLowerCase();

      if (!(customer[name] || '').trim()) {
        list.push({ field: name, label: fieldName });
      } else if (ui.errors[name]) {
        // Present but wrong, which is a different problem from absent and should not read the
        // same. The field's own error message says what specifically is wrong.
        list.push({ field: name, label: `a valid ${fieldName}` });
      }
    });

    if (!consent.agreedToTerms) {
      list.push({ field: 'agreedToTerms', label: 'terms agreement' });
    }

    return list;
  }, [selection.primaryPlanId, customer, consent.agreedToTerms, ui.errors]);

  // ---------------------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------------------

  // `validate` is passed INTO the action rather than imported by the reducer, keeping the
  // reducer free of the validation module and letting tests use a stub validator.
  const onFieldChange = useCallback((field, value) => {
    dispatch({ type: ACTIONS.FIELD_CHANGED, field, value, validate: validateField });
  }, []);

  const onFieldBlur = useCallback((field, value) => {
    dispatch({ type: ACTIONS.FIELD_BLURRED, field, value, validate: validateField });
  }, []);

  const handlers = useMemo(
    () => ({
      onFieldChange,
      onFieldBlur,
      selectPlan: (planId) => dispatch({ type: ACTIONS.PLAN_SELECTED, planId }),
      selectTerm: (term) => dispatch({ type: ACTIONS.TERM_SELECTED, term }),
      toggleAddon: (id) => dispatch({ type: ACTIONS.ADDON_TOGGLED, id }),
      toggleOneTime: (id) => dispatch({ type: ACTIONS.ONETIME_TOGGLED, id }),
      answerRadio: (questionId, answerId) =>
        dispatch({ type: ACTIONS.QUESTION_ANSWERED_RADIO, questionId, answerId }),
      answerCheckbox: (questionId, answerId) =>
        dispatch({ type: ACTIONS.QUESTION_ANSWERED_CHECKBOX, questionId, answerId }),
      setAgreedToTerms: (value) => dispatch({ type: ACTIONS.TERMS_TOGGLED, value }),
      setMailchimpOptIn: (value) => dispatch({ type: ACTIONS.MAILCHIMP_TOGGLED, value }),
      setCouponCode: (value) => dispatch({ type: ACTIONS.COUPON_CODE_CHANGED, value }),
      expandCoupon: () => dispatch({ type: ACTIONS.COUPON_FIELD_EXPANDED }),
      cancelCoupon: () => dispatch({ type: ACTIONS.COUPON_FIELD_CANCELLED }),
      dismissResume: () => dispatch({ type: ACTIONS.RESUME_DISMISSED }),
      dismissStaleLink: () => dispatch({ type: ACTIONS.STALE_LINK_DISMISSED }),
      removeCoupon: () => dispatch({ type: ACTIONS.COUPON_REMOVED }),
    }),
    [onFieldChange, onFieldBlur]
  );

  /**
   * Validate everything on submit. Terms is not a customer field, so it is checked separately.
   * Returns the errors so the caller can decide what to focus.
   */
  const validateAll = useCallback(() => {
    const { errors } = validateCustomer(customer);

    if (!consent.agreedToTerms) {
      errors.agreedToTerms =
        'You must agree to the Terms of Use and Privacy Policy to continue';
    }

    if (Object.keys(errors).length > 0) {
      dispatch({ type: ACTIONS.SUBMIT_BLOCKED, errors });
      return errors;
    }
    return null;
  }, [customer, consent.agreedToTerms]);

  const submitLifecycle = useMemo(
    () => ({
      started: () => dispatch({ type: ACTIONS.SUBMIT_STARTED }),
      failed: (message, errors) => dispatch({ type: ACTIONS.SUBMIT_FAILED, message, errors }),
      settled: () => dispatch({ type: ACTIONS.SUBMIT_SETTLED }),
      /**
       * The ONE moment customer PII is persisted: immediately before leaving for Stripe, so a
       * cancelled payment can be resumed. Called from the submit handler, not an effect.
       */
      persistForResume: () => persistForCheckout({ selection, customer, coupon, consent }),
    }),
    [selection, customer, coupon, consent]
  );

  /**
   * The shareable URL, built from the same state the form renders.
   *
   * Exposed here rather than assembled inside ShareConfigButton, which previously received a
   * 12-field prop object it had to reassemble. Now the builder and the parser sit side by side
   * in urlPrefill.js, which is what makes the round-trip test possible -- and that test is what
   * would have caught questionAnswers being silently dropped from every shared link.
   */
  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const params = buildShareParams({ selection, customer, coupon });
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }, [selection, customer, coupon]);

  return {
    // state slices
    customer,
    selection,
    coupon,
    consent,
    ui,
    // derived
    cart,
    questions,
    visiblePrimary,
    visibleAddons,
    visibleOnetime,
    blockers,
    shareUrl,
    canSubmit: blockers.length === 0 && !ui.submitting,
    // actions
    handlers,
    validateAll,
    submitLifecycle,
    dispatch,
  };
}

/**
 * Applying a coupon needs the network, so it lives here rather than in the reducer (which must
 * stay pure). Exported separately so it can be tested with a stubbed fetch.
 */
export async function applyCoupon(dispatch, code) {
  if (!code.trim()) {
    dispatch({ type: ACTIONS.COUPON_REJECTED, message: 'Please enter a coupon code' });
    return;
  }

  dispatch({ type: ACTIONS.COUPON_VALIDATING });

  try {
    const response = await fetch('/api/validate-coupon', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code.trim() }),
    });

    if (!response.ok) {
      let message = 'Invalid coupon code';
      try {
        const data = await response.json();
        if (data?.error || data?.message) message = data.error || data.message;
      } catch {
        // A non-JSON error body is not itself worth reporting; the generic message stands.
      }
      dispatch({ type: ACTIONS.COUPON_REJECTED, message });
      return;
    }

    const data = await response.json();
    if (data.valid) {
      dispatch({ type: ACTIONS.COUPON_APPLIED, coupon: data });
    } else {
      dispatch({ type: ACTIONS.COUPON_REJECTED, message: data.error || 'Invalid coupon code' });
    }
  } catch (err) {
    console.error('Coupon validation error:', err);
    dispatch({ type: ACTIONS.COUPON_REJECTED, message: 'Failed to validate coupon code' });
  }
}
